import * as React from "react";
import { cn } from "@/lib/utils";

interface FloatingInputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
}

const FloatingInput = React.forwardRef<HTMLInputElement, FloatingInputProps>(
  ({ className, label, ...props }, ref) => {
    const [isFocused, setIsFocused] = React.useState(false);
    const [hasValue, setHasValue] = React.useState(!!props.value || !!props.defaultValue);

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      props.onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      setHasValue(!!e.target.value);
      props.onBlur?.(e);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setHasValue(!!e.target.value);
      props.onChange?.(e);
    };

    const isLabelVisible = isFocused || hasValue;

    return (
      <div className="relative w-full">
        <input
          ref={ref}
          {...props}
          placeholder={label ? undefined : props.placeholder}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onChange={handleChange}
          className={cn(
            "w-full h-14 bg-white border border-[#6B797A] rounded px-4 text-base font-normal text-[#151C1D] outline-none transition-all focus:border-[#00646B] focus:border-[3px]",
            isLabelVisible ? "pt-4 pb-2" : "",
            className,
          )}
        />
        {label && (
          <label
            className={cn(
              "absolute left-4 transition-all duration-150 pointer-events-none font-normal",
              isLabelVisible
                ? "text-xs text-[#00646B] -top-2 px-1 bg-white"
                : "text-base text-[#3B494A] top-1/2 -translate-y-1/2",
            )}
          >
            {label}
          </label>
        )}
      </div>
    );
  },
);

FloatingInput.displayName = "FloatingInput";

export { FloatingInput };
